import { auth } from '../contexts/authentication';
import axios from 'axios';

// Configurações do NocoDB
const NOCODB_URL = import.meta.env.VITE_NOCODB_URL;
const NOCODB_AUTH_TOKEN = import.meta.env.VITE_NOCODB_AUTH_TOKEN;
const PROJECT_NAME = import.meta.env.VITE_NOCODB_PROJECT;
const TABLE_NAME = 'forms';

// Codifica o nome do projeto para ser seguro na URL
const encodedProjectName = encodeURIComponent(PROJECT_NAME);

// Configuração do cliente Axios
const nocodbClient = axios.create({
  baseURL: NOCODB_URL,
  headers: {
    'xc-token': NOCODB_AUTH_TOKEN, 
    'Content-Type': 'application/json'
  }
});

const buildTableUrl = (tableName: string) => `/db/data/noco/${encodedProjectName}/${tableName}`;

const formatItemsToString = (items: any[], formatter: (item: any) => string): string | null => {
  if (!Array.isArray(items) || items.length === 0) {
    return null;
  }
  return items.map(formatter).join('\n\n---\n\n');
};

export const submitFormToNocoDB = async (formData: any) => {
  const user = auth.currentUser;

  try {
    const productsAsString = formatItemsToString(
      formData.productsServices,
      (p) => `Produto/Serviço: ${p.name}\nDescrição: ${p.description}\nPreço: ${p.price}`
    );
    const faqsAsString = formatItemsToString(
      formData.faqs,
      (f) => `Pergunta: ${f.question}\nResposta: ${f.answer}`
    );
    const preparedData = {
      user_name: user?.displayName || '',
      user_email: user?.email || '',
      business_name: formData.businessName,
      business_industry: formData.businessIndustry,
      business_description: formData.businessDescription,
      target_audience: formData.targetAudience,
      business_differentials: formData.businessDifferentials,
      assistant_name: formData.assistantName,
      tone_of_voice: Array.isArray(formData.toneOfVoice) ? formData.toneOfVoice.join(', ') : formData.toneOfVoice,
      emoji_usage: formData.emojiUsage,
      client_addressing: formData.clientAddressing,
      working_hours: formData.workingHours,
      business_location: formData.businessLocation,
      google_maps_link: formData.googleMapsLink,
      phone_contact: formData.phoneContact,
      email_contact: formData.emailContact,
      website_url: formData.websiteUrl,
      social_media: formData.socialMedia,
      payment_methods: formData.paymentMethods,
      delivery_areas: formData.deliveryAreas,
      delivery_fee: formData.deliveryFee,
      delivery_time: formData.deliveryTime,
      main_objective: formData.mainObjective,
      appointment_tool: formData.appointmentTool,
      forbidden_topics: formData.forbiddenTopics,
      transfer_conditions: Array.isArray(formData.transferConditions) ? formData.transferConditions.join(', ') : formData.transferConditions,
      transfer_message: formData.transferMessage,
      submission_date: new Date().toISOString(),
      status: 'pending',
      products_services: productsAsString,
      faqs: faqsAsString,
    };
    const response = await nocodbClient.post(buildTableUrl(TABLE_NAME), preparedData);
    return response.data;
  } catch (error: any) {
    console.error('Erro ao enviar dados para o NocoDB:', error.response ? error.response.data : error.message);
    throw new Error(`Falha ao salvar dados: ${error.message}`);
  }
};

export const getFormSubmissions = async (page = 1, limit = 20) => {
  try {
    const response = await nocodbClient.get(
      buildTableUrl(TABLE_NAME), {
        params: {
          limit,
          offset: (page - 1) * limit,
          sort: '-Id'
        }
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('Erro ao obter submissões:', error.response ? error.response.data : error.message);
    throw new Error(`Falha ao carregar dados: ${error.message}`);
  }
};

export const getFormSubmissionById = async (id: string) => {
  try {
    const response = await nocodbClient.get(`${buildTableUrl(TABLE_NAME)}/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(`Erro ao obter submissão ${id}:`, error.message);
    throw new Error(`Falha ao carregar detalhes: ${error.message}`);
  }
};

export const updateSubmissionStatus = async (id: string, status: string) => {
  try {
    const response = await nocodbClient.patch(
      `${buildTableUrl(TABLE_NAME)}/${id}`,
      { status }
    );
    return response.data;
  } catch (error: any) {
    console.error(`Erro ao atualizar status da submissão ${id}:`, error.message);
    throw new Error(`Falha ao atualizar status: ${error.message}`);
  }
};

/**
 * --- FUNÇÃO CORRIGIDA ---
 * Atualiza um registro de submissão inteiro por ID.
 * @param id O ID da submissão a ser atualizada.
 * @param data O objeto completo com os dados atualizados.
 */
export const updateSubmission = async (id: string, data: any) => {
  try {
    // Cria uma cópia dos dados para não modificar o objeto original
    const updateData = { ...data };

    // --- CORREÇÃO: Remover campos gerenciados pelo NocoDB antes de enviar ---
    // A API do NocoDB rejeita patches que tentam atualizar esses campos.
    delete updateData.Id;
    delete updateData.CreatedAt;
    delete updateData.UpdatedAt;
    // Opcional: se você tiver campos de relacionamento (links), pode ser necessário removê-los também
    // delete updateData.NomeDoRelacionamento;

    const response = await nocodbClient.patch(
      `${buildTableUrl(TABLE_NAME)}/${id}`,
      updateData // Envia apenas os dados que podem ser modificados
    );
    return response.data;
  } catch (error: any) {
    // Log detalhado para facilitar a depuração
    console.error(`Erro ao atualizar submissão ${id}:`, error.response ? error.response.data : error.message);
    throw new Error(`Falha ao atualizar dados: ${error.response?.data?.msg || error.message}`);
  }
};

export const deleteSubmission = async (id: string) => {
  try {
    const response = await nocodbClient.delete(
      `${buildTableUrl(TABLE_NAME)}/${id}`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Erro ao excluir submissão ${id}:`, error.response ? error.response.data : error.message);
    throw new Error(`Falha ao excluir dados: ${error.response?.data?.msg || error.message}`);
  }
};